<template>
  <div class="container admin-gpx-config">
    <h1 class="hover" v-to="'/admin/events/' + eventId + '/registrations'">← Voltar ao evento</h1>
    <h2>Configuração de GPX</h2>

    <!-- Event-level upload window -->
    <section class="panel">
      <h3>Janela de envio (todo o evento)</h3>
      <label class="toggle">
        <input type="checkbox" v-model="ev.uploadGPX" />
        Habilitar envio de GPX neste evento
      </label>
      <div class="dates" :class="{ disabled: !ev.uploadGPX }">
        <label>Abertura
          <input type="datetime-local" v-model="ev.openAt" :disabled="!ev.uploadGPX" />
        </label>
        <label>Fechamento
          <input type="datetime-local" v-model="ev.closeAt" :disabled="!ev.uploadGPX" />
        </label>
      </div>
      <button class="btn btn-primary" :disabled="ev.saving" @click="saveEvent">
        {{ ev.saving ? 'Salvando...' : 'Salvar janela de envio' }}
      </button>
      <span v-if="ev.msg" class="msg">{{ ev.msg }}</span>
    </section>

    <!-- Per-competition route + race times -->
    <h3 class="section-title">Percursos por prova</h3>
    <div v-if="loading" class="muted">Carregando...</div>

    <div v-for="comp in competitions" :key="comp.competition_id" class="comp-card">
      <div class="comp-head">
        <h4>{{ comp.competition_name }}</h4>
        <span v-if="isConfigured(comp)" class="badge ok">✅ configurada</span>
        <span v-else-if="comp.has_track" class="badge wait">⚠ faltam horários</span>
        <span v-else class="badge bad">sem percurso</span>
      </div>

      <!-- Route -->
      <div class="row-line">
        <div class="route-status">
          <strong>Percurso:</strong>
          <span v-if="comp.has_track">{{ comp.route_km }} km · {{ comp.route_points }} pts</span>
          <span v-else class="muted">não definido</span>
          <span v-if="comp._pending"> → <em>novo: {{ comp._pending.points }} pts (não salvo)</em></span>
        </div>
        <div class="route-actions">
          <label class="file-btn">
            <input type="file" accept=".gpx,.geojson,.json" @change="onFile(comp, $event)" />
            <span>{{ comp.has_track ? 'trocar arquivo' : 'escolher .gpx/.geojson' }}</span>
          </label>
          <select v-if="otherTracks(comp).length" v-model="comp._copyFrom" @change="copyFrom(comp)">
            <option :value="null">copiar de…</option>
            <option v-for="o in otherTracks(comp)" :key="o.competition_id" :value="o.competition_id">
              {{ o.competition_name }}
            </option>
          </select>
          <button v-if="comp.has_track || comp._pending" class="link" @click="toggleMap(comp)">
            {{ comp._showMap ? 'esconder mapa' : 'ver no mapa' }}
          </button>
        </div>
      </div>
      <div v-if="comp._err" class="err">{{ comp._err }}</div>

      <!-- Inline map preview -->
      <div v-if="comp._showMap && comp._mapRoute" class="map-preview">
        <mapbox-track
          :key="'map-' + comp.competition_id + '-' + (comp._pending ? 'p' : 's')"
          :reference-geo="comp._mapRoute"
          :start-point="comp._mapStart"
          :finish-point="comp._mapFinish"
        />
      </div>

      <!-- Race times (required) -->
      <div class="race-times">
        <label>Largada *
          <input type="datetime-local" v-model="comp._startLocal" />
        </label>
        <label>Corte de chegada *
          <input type="datetime-local" v-model="comp._cutoffLocal" />
        </label>
        <button class="btn btn-secondary" :disabled="comp._busy || !comp.has_track" @click="saveTimes(comp)">
          Salvar horários
        </button>
        <small v-if="!comp.has_track" class="muted">suba o percurso primeiro</small>
      </div>

      <!-- Advanced thresholds -->
      <details class="advanced">
        <summary>Limites avançados</summary>
        <div class="thr-grid">
          <label>
            <span class="thr-label">Cobertura mín. (%)
              <span class="info" v-b-tooltip.hover title="Percentual mínimo do percurso oficial que o GPX do atleta precisa cobrir. Abaixo disso o envio é marcado como suspeito (possível corte de trajeto). Padrão: 70% (provas longas acumulam gaps de GPS/bateria).">ⓘ</span>
            </span>
            <input type="number" v-model.number="comp._thr.minCoveragePercent" placeholder="70" />
          </label>
          <label>
            <span class="thr-label">Vel. máx (km/h)
              <span class="info" v-b-tooltip.hover title="Velocidade máxima sustentada (média em janela de 30s, não pico) plausível correndo. Trechos mais rápidos são sinalizados como suspeitos (ex.: percurso feito de carro ou bicicleta). Padrão: 20 km/h.">ⓘ</span>
            </span>
            <input type="number" v-model.number="comp._thr.maxRunningKmh" placeholder="20" />
            <small class="pace-hint">ritmo ≈ {{ paceFor(comp._thr.maxRunningKmh || 20) }} min/km</small>
          </label>
          <label>
            <span class="thr-label">Buffer da rota (m)
              <span class="info" v-b-tooltip.hover title="Largura do corredor de tolerância ao redor do percurso oficial. Pontos do GPX dentro dessa faixa contam como 'em cima da rota'. Maior = mais tolerante a desvio de GPS. Padrão: 50 m.">ⓘ</span>
            </span>
            <input type="number" v-model.number="comp._thr.routeBufferM" placeholder="50" />
          </label>
          <label>
            <span class="thr-label">Tolerância janela (min)
              <span class="info" v-b-tooltip.hover title="Folga de tempo antes da largada e depois do corte de chegada em que o envio ainda é aceito sem alerta de horário. Padrão: 120 min.">ⓘ</span>
            </span>
            <input type="number" v-model.number="comp._thr.raceWindowGraceMin" placeholder="120" />
          </label>
          <label>
            <span class="thr-label">Raio largada (m)
              <span class="info" v-b-tooltip.hover title="Distância máxima do ponto de largada em que o primeiro ponto do GPX é aceito como início válido da prova. Padrão: 100 m.">ⓘ</span>
            </span>
            <input type="number" v-model.number="comp._startRadius" placeholder="100" />
          </label>
          <label>
            <span class="thr-label">Raio chegada (m)
              <span class="info" v-b-tooltip.hover title="Distância máxima do ponto de chegada em que o último ponto do GPX é aceito como fim válido da prova. Padrão: 100 m.">ⓘ</span>
            </span>
            <input type="number" v-model.number="comp._finishRadius" placeholder="100" />
          </label>
        </div>
      </details>

      <div class="comp-save">
        <button class="btn btn-primary" :disabled="comp._busy || (!comp._pending && !comp.has_track)" @click="saveTrack(comp)">
          {{ comp._busy ? 'Salvando...' : (comp._pending ? 'Salvar percurso e limites' : 'Salvar limites') }}
        </button>
        <span v-if="comp._msg" class="msg">{{ comp._msg }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import MapboxTrack from '@/components/Map/mapbox-track.vue';
import { parseRouteFile } from '@/util/parse-route-file';

export default {
  name: 'admin-gpx-config',
  components: { MapboxTrack },

  data() {
    return {
      loading: true,
      ev: { uploadGPX: false, openAt: '', closeAt: '', saving: false, msg: '' },
      competitions: [],
    };
  },

  computed: {
    eventId() { return parseInt(this.$route.params.eventid); },
  },

  async created() {
    await Promise.all([this.loadEvent(), this.loadCompetitions()]);
    this.loading = false;
  },

  methods: {
    // ---- datetime helpers ----
    isoToLocal(iso) {
      if (!iso) return '';
      const d = new Date(iso); if (isNaN(d)) return '';
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    localToIso(s) { if (!s) return null; const d = new Date(s); return isNaN(d) ? null : d.toISOString(); },

    // Converte velocidade (km/h) no ritmo equivalente "m:ss" por km.
    paceFor(kmh) {
      const v = Number(kmh);
      if (!v || !isFinite(v) || v <= 0) return '—';
      const total = Math.round(3600 / v);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },

    // ---- event upload window ----
    async loadEvent() {
      const r = await api.getJSONAsync('gpxGetEventConfig', { eventId: this.eventId });
      if (r && !r.error) {
        this.ev.uploadGPX = !!r.uploadGPX;
        this.ev.openAt = this.isoToLocal(r.gpxUploadOpenAt);
        this.ev.closeAt = this.isoToLocal(r.gpxUploadCloseAt);
      }
    },
    async saveEvent() {
      this.ev.saving = true; this.ev.msg = '';
      try {
        const r = await api.postJSONAsync('gpxSetEventConfig', {
          eventId: this.eventId, uploadGPX: this.ev.uploadGPX,
          gpxUploadOpenAt: this.localToIso(this.ev.openAt),
          gpxUploadCloseAt: this.localToIso(this.ev.closeAt),
        });
        this.ev.msg = (r && r.error) ? ('Erro: ' + r.message) : 'Salvo ✓';
      } finally { this.ev.saving = false; }
    },

    // ---- competitions ----
    async loadCompetitions() {
      const r = await api.getJSONAsync('gpxListCompetitionTracks', { eventId: this.eventId });
      if (r && !r.error) {
        this.competitions = (r.rows || []).map((c) => this.decorate(c));
      }
    },
    decorate(c) {
      const thr = c.thresholds || {};
      return Object.assign({}, c, {
        _pending: null, _err: '', _msg: '', _busy: false,
        _showMap: false, _mapRoute: null, _mapStart: null, _mapFinish: null,
        _copyFrom: null,
        _startLocal: this.isoToLocal(c.race_start_at),
        _cutoffLocal: this.isoToLocal(c.race_finish_cutoff_at),
        _thr: {
          minCoveragePercent: thr.minCoveragePercent != null ? thr.minCoveragePercent : null,
          maxRunningKmh: thr.maxRunningKmh != null ? thr.maxRunningKmh : null,
          routeBufferM: thr.routeBufferM != null ? thr.routeBufferM : null,
          raceWindowGraceMin: thr.raceWindowGraceMin != null ? thr.raceWindowGraceMin : null,
        },
        _startRadius: c.start_radius_m || null,
        _finishRadius: c.finish_radius_m || null,
      });
    },
    isConfigured(c) { return c.has_track && c.race_start_at && c.race_finish_cutoff_at; },
    otherTracks(c) {
      return this.competitions.filter((o) => o.competition_id !== c.competition_id && o.has_track);
    },

    onFile(comp, ev) {
      comp._err = ''; comp._msg = '';
      const f = ev.target.files && ev.target.files[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = parseRouteFile(reader.result, f.name);
          this.$set(comp, '_pending', parsed);
          comp._mapRoute = parsed.route;
          comp._mapStart = parsed.startPoint;
          comp._mapFinish = parsed.finishPoint;
        } catch (e) {
          comp._err = e.message || 'Falha ao ler o arquivo';
        }
      };
      reader.readAsText(f);
    },

    buildThresholds(comp) {
      const out = {};
      const t = comp._thr;
      for (const k of Object.keys(t)) {
        if (t[k] != null && t[k] !== '' && isFinite(t[k])) out[k] = Number(t[k]);
      }
      return out;
    },

    async saveTrack(comp) {
      comp._busy = true; comp._msg = ''; comp._err = '';
      try {
        let route, startPoint, finishPoint;
        if (comp._pending) {
          route = comp._pending.route; startPoint = comp._pending.startPoint; finishPoint = comp._pending.finishPoint;
        } else {
          // Re-save thresholds on the existing route — fetch it.
          const cur = await api.getJSONAsync('competitionTrackGet', { competitionId: comp.competition_id });
          if (!cur || cur.error || !cur.route) { comp._err = 'Não foi possível carregar o percurso atual'; return; }
          route = cur.route; startPoint = cur.startPoint; finishPoint = cur.finishPoint;
        }
        const r = await api.postJSONAsync('gpxSetCompetitionTrack', {
          competitionId: comp.competition_id, route, startPoint, finishPoint,
          thresholds: this.buildThresholds(comp),
          startRadiusM: comp._startRadius || 100,
          finishRadiusM: comp._finishRadius || 100,
        });
        if (r && r.error) throw new Error(r.message);
        // Salva os horários no mesmo clique, se preenchidos — evita o
        // "faltam horários" logo após salvar o percurso.
        if (comp._startLocal && comp._cutoffLocal) {
          const rt = await api.postJSONAsync('gpxSetCompetitionRaceTimes', {
            competitionId: comp.competition_id,
            raceStartAt: this.localToIso(comp._startLocal),
            raceFinishCutoffAt: this.localToIso(comp._cutoffLocal),
          });
          if (rt && rt.error) throw new Error(rt.message);
        }
        comp._msg = 'Percurso salvo ✓';
        await this.loadCompetitions();
      } catch (e) {
        comp._err = 'Erro: ' + (e.message || e);
      } finally { comp._busy = false; }
    },

    async saveTimes(comp) {
      comp._busy = true; comp._msg = ''; comp._err = '';
      try {
        if (!comp._startLocal || !comp._cutoffLocal) { comp._err = 'Preencha largada e corte'; return; }
        const r = await api.postJSONAsync('gpxSetCompetitionRaceTimes', {
          competitionId: comp.competition_id,
          raceStartAt: this.localToIso(comp._startLocal),
          raceFinishCutoffAt: this.localToIso(comp._cutoffLocal),
        });
        if (r && r.error) throw new Error(r.message);
        comp._msg = 'Horários salvos ✓';
        await this.loadCompetitions();
      } catch (e) {
        comp._err = 'Erro: ' + (e.message || e);
      } finally { comp._busy = false; }
    },

    async copyFrom(comp) {
      const fromId = comp._copyFrom;
      comp._copyFrom = null;
      if (!fromId) return;
      if (!confirm('Copiar o percurso desta prova? Os horários NÃO são copiados.')) return;
      comp._busy = true; comp._err = '';
      try {
        const r = await api.postJSONAsync('gpxCopyCompetitionTrack', {
          fromCompetitionId: fromId, toCompetitionId: comp.competition_id,
        });
        if (r && r.error) throw new Error(r.message);
        comp._msg = 'Percurso copiado ✓';
        await this.loadCompetitions();
      } catch (e) {
        comp._err = 'Erro: ' + (e.message || e);
      } finally { comp._busy = false; }
    },

    async toggleMap(comp) {
      comp._showMap = !comp._showMap;
      if (!comp._showMap) return;
      if (comp._pending) {
        comp._mapRoute = comp._pending.route;
        comp._mapStart = comp._pending.startPoint;
        comp._mapFinish = comp._pending.finishPoint;
        return;
      }
      if (!comp._mapRoute) {
        const cur = await api.getJSONAsync('competitionTrackGet', { competitionId: comp.competition_id });
        if (cur && !cur.error) {
          comp._mapRoute = cur.route; comp._mapStart = cur.startPoint; comp._mapFinish = cur.finishPoint;
        }
      }
    },
  },
};
</script>

<style scoped lang="stylus">
.admin-gpx-config
  padding: 1.5em 0

.panel
  margin: 1em 0 1.5em
  padding: 1em 1.2em
  background: rgba(23,162,184,0.06)
  border: 1px solid rgba(23,162,184,0.25)
  border-radius: 8px

.section-title
  margin-top: 1.5em

.toggle
  display: block
  margin-bottom: 0.7em
  font-weight: 600

.dates, .race-times
  display: flex
  gap: 1.2em
  flex-wrap: wrap
  align-items: flex-end
  margin-bottom: 0.9em

  &.disabled
    opacity: 0.5

  label
    display: flex
    flex-direction: column
    font-size: 0.85em
    gap: 0.25em

  input
    padding: 0.4em
    border: 1px solid #ccc
    border-radius: 4px

.comp-card
  margin-bottom: 1em
  padding: 1em 1.2em
  border: 1px solid rgba(0,0,0,0.12)
  border-radius: 8px

.comp-head
  display: flex
  align-items: center
  gap: 0.7em
  margin-bottom: 0.6em

  h4
    margin: 0

.row-line
  display: flex
  justify-content: space-between
  align-items: center
  gap: 1em
  flex-wrap: wrap
  margin-bottom: 0.6em

.route-actions
  display: flex
  align-items: center
  gap: 0.7em
  flex-wrap: wrap

.file-btn
  cursor: pointer

  input[type=file]
    display: none

  span
    display: inline-block
    padding: 0.4em 0.9em
    background: #17a2b8
    color: #fff
    border-radius: 6px
    font-size: 0.85em

.route-actions select
  padding: 0.35em
  border: 1px solid #ccc
  border-radius: 4px
  font-size: 0.85em

.map-preview
  margin: 0.6em 0

.advanced
  margin: 0.6em 0

  summary
    cursor: pointer
    font-size: 0.88em
    color: #17a2b8

.thr-grid
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 0.7em
  margin-top: 0.6em

  @media (max-width: 700px)
    grid-template-columns: 1fr 1fr

  label
    display: flex
    flex-direction: column
    font-size: 0.8em
    gap: 0.2em

  input
    padding: 0.3em
    border: 1px solid #ccc
    border-radius: 4px

  .thr-label
    display: inline-flex
    align-items: center
    gap: 0.3em

  .info
    cursor: help
    color: #17a2b8
    font-size: 0.95em
    line-height: 1

  .pace-hint
    color: #17a2b8
    font-size: 0.9em
    margin-top: 0.1em

.comp-save
  margin-top: 0.6em
  display: flex
  align-items: center
  gap: 0.8em

.btn
  padding: 0.45em 1em
  border-radius: 5px
  border: none
  cursor: pointer
  font-size: 0.9em

.btn-primary
  background: #007bff
  color: #fff
.btn-secondary
  background: #17a2b8
  color: #fff
.btn:disabled
  opacity: 0.5
  cursor: default

.link
  background: none
  border: none
  color: #17a2b8
  cursor: pointer
  font-size: 0.85em

.badge
  padding: 0.15em 0.5em
  border-radius: 4px
  font-size: 0.8em
.badge.ok
  background: #d1e7dd
  color: #0f5132
.badge.wait
  background: #fff3cd
  color: #664d03
.badge.bad
  background: #f8d7da
  color: #842029

.msg
  color: #0f5132
  font-size: 0.88em
.err
  color: #842029
  font-size: 0.85em
  margin: 0.3em 0
.muted
  color: #999
.hover
  cursor: pointer
</style>
